import React, { useState } from 'react';
import Logo from './Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const RegistrasiForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    namaLengkap: '',
    email: '',
    noHp: '',
    alamat: '',
    jenisKelamin: '',
    tanggalLahir: '',
    pekerjaan: '',
    sumberInfo: '',
    minatInvestasi: '',
    password: '',
    passwordConfirmation: '',
    minatTabungan: false,
    minatKonsultasi: false,
    minatProduk: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.namaLengkap.trim()) {
      newErrors.namaLengkap = 'Nama lengkap wajib diisi';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (!formData.noHp.trim()) {
      newErrors.noHp = 'Nomor HP wajib diisi';
    } else if (!/^08\d{8,11}$/.test(formData.noHp.replace(/\s/g, ''))) {
      newErrors.noHp = 'Format nomor HP tidak valid (contoh: 081234567890)';
    }

    if (!formData.alamat.trim()) {
      newErrors.alamat = 'Alamat wajib diisi';
    }

    if (!formData.jenisKelamin) {
      newErrors.jenisKelamin = 'Jenis kelamin wajib dipilih';
    }

    if (!formData.tanggalLahir) {
      newErrors.tanggalLahir = 'Tanggal lahir wajib diisi';
    }

    if (!formData.pekerjaan.trim()) {
      newErrors.pekerjaan = 'Pekerjaan wajib diisi';
    }

    if (!formData.sumberInfo) {
      newErrors.sumberInfo = 'Sumber informasi wajib dipilih';
    }

    if (!formData.minatInvestasi) {
      newErrors.minatInvestasi = 'Minat investasi wajib dipilih';
    }

    // Validasi password
    if (!formData.password.trim()) {
      newErrors.password = 'Password wajib diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }

    if (!formData.passwordConfirmation.trim()) {
      newErrors.passwordConfirmation = 'Konfirmasi password wajib diisi';
    } else if (formData.password !== formData.passwordConfirmation) {
      newErrors.passwordConfirmation = 'Password tidak cocok';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format tanggal lahir dari YYYY-MM-DD (dari input date) ke format yang diharapkan API
      let birthDateFormatted = formData.tanggalLahir;
      if (formData.tanggalLahir) {
        // Pastikan format YYYY-MM-DD (input date sudah otomatis format ini)
        birthDateFormatted = formData.tanggalLahir;
      }

      // Map data form ke format yang diharapkan API
      const requestData = {
        full_name: formData.namaLengkap.trim(),
        email: formData.email.trim() || null,
        phone_no: formData.noHp.trim() || null,
        gender: formData.jenisKelamin || null,
        birth_date: birthDateFormatted || null,
        occupation: formData.pekerjaan.trim() || null,
        alamat: formData.alamat.trim() || null,
        source: formData.sumberInfo || null,
        budget: formData.minatInvestasi || null,
        password: formData.password || null
      };

      // Panggil API
      const response = await apiClient.post(API_ENDPOINTS.USER.CREATE, requestData);
      
      // Success notification
      alert('Registrasi berhasil! Tim kami akan menghubungi Anda dalam 1x24 jam.');
      
      // Reset form
      setFormData({
        namaLengkap: '',
        email: '',
        noHp: '',
        alamat: '',
        jenisKelamin: '',
        tanggalLahir: '',
        pekerjaan: '',
        sumberInfo: '',
        minatInvestasi: '',
        password: '',
        passwordConfirmation: '',
        minatTabungan: false,
        minatKonsultasi: false,
        minatProduk: false
      });

      // Close modal
      onBack();
    } catch (error) {
      console.error('Registrasi error:', error);
      
      // Handle error berdasarkan status code
      if (error.status === 400 || error.status === 422) {
        // Validation error
        const errorMessage = error.data?.detail?.[0]?.msg || 
                            error.data?.message || 
                            error.message || 
                            'Data tidak valid. Periksa kembali form Anda.';
        alert(errorMessage);
      } else if (error.status === 409) {
        alert('Email sudah terdaftar. Silakan gunakan email lain.');
      } else {
        alert(error.message || 'Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-2 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-sm lg:max-w-md xl:max-w-lg my-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-3 lg:p-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button 
                onClick={onBack}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-lg"></i>
              </button>
              <div className="flex items-center space-x-1">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-base">Registrasi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-3 lg:p-4">
          <form onSubmit={handleSubmit} className="space-y-3 lg:space-y-4">
            {/* Personal Information */}
            <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
              <h3 className="text-primary font-bold text-sm lg:text-base mb-2 lg:mb-3 flex items-center">
                <i className="fa-solid fa-user mr-1 text-xs"></i>
                Informasi Personal
              </h3>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nama Lengkap *
                  </label>
                  <input
                    type="text"
                    name="namaLengkap"
                    value={formData.namaLengkap}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.namaLengkap ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Nama lengkap"
                  />
                  {errors.namaLengkap && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.namaLengkap}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="email@contoh.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nomor HP *
                    </label>
                    <input
                      type="tel"
                      name="noHp"
                      value={formData.noHp}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.noHp ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="08123456789"
                    />
                    {errors.noHp && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.noHp}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Jenis Kelamin *
                    </label>
                    <select
                      name="jenisKelamin"
                      value={formData.jenisKelamin}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.jenisKelamin ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Pilih</option>
                      <option value="Laki-laki">Laki-laki</option>
                      <option value="Perempuan">Perempuan</option>
                    </select>
                    {errors.jenisKelamin && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.jenisKelamin}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tanggal Lahir *
                    </label>
                    <input
                      type="date"
                      name="tanggalLahir"
                      value={formData.tanggalLahir}
                      onChange={handleInputChange}
                      className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.tanggalLahir ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.tanggalLahir && (
                      <p className="text-red-500 text-xs mt-0.5">{errors.tanggalLahir}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pekerjaan *
                  </label>
                  <input
                    type="text"
                    name="pekerjaan"
                    value={formData.pekerjaan}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.pekerjaan ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Pekerjaan"
                  />
                  {errors.pekerjaan && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.pekerjaan}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Alamat *
                  </label>
                  <textarea
                    name="alamat"
                    value={formData.alamat}
                    onChange={handleInputChange}
                    rows={2}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.alamat ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Alamat lengkap"
                  />
                  {errors.alamat && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.alamat}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Minimal 6 karakter"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Konfirmasi Password *
                  </label>
                  <input
                    type="password"
                    name="passwordConfirmation"
                    value={formData.passwordConfirmation}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.passwordConfirmation ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Ulangi password"
                  />
                  {errors.passwordConfirmation && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.passwordConfirmation}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Interest Information */}
            <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
              <h3 className="text-primary font-bold text-sm lg:text-base mb-2 lg:mb-3 flex items-center">
                <i className="fa-solid fa-heart mr-1 text-xs"></i>
                Minat & Informasi
              </h3>
              
              <div className="space-y-2 lg:space-y-3">
                <div>
                  <label className="block text-xs lg:text-sm font-medium text-gray-700 mb-1">
                    Dari mana Anda mengetahui PAK MoO? *
                  </label>
                  <select
                    name="sumberInfo"
                    value={formData.sumberInfo}
                    onChange={handleInputChange}
                    className={`w-full px-2 lg:px-3 py-1.5 lg:py-2 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs lg:text-sm ${errors.sumberInfo ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Pilih sumber</option>
                    <option value="Google">Google</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Teman/Keluarga">Teman/Keluarga</option>
                    <option value="Brosur/Flyer">Brosur/Flyer</option>
                    <option value="Event/Pameran">Event/Pameran</option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                  {errors.sumberInfo && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.sumberInfo}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Budget investasi? *
                  </label>
                  <select
                    name="minatInvestasi"
                    value={formData.minatInvestasi}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1.5 border rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors text-xs ${errors.minatInvestasi ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Pilih budget</option>
                    <option value="dibawah-5-juta">Dibawah Rp 5 juta</option>
                    <option value="5-10-juta">Rp 5-10 juta</option>
                    <option value="10-25-juta">Rp 10-25 juta</option>
                    <option value="25-50-juta">Rp 25-50 juta</option>
                    <option value="diatas-50-juta">Diatas Rp 50 juta</option>
                  </select>
                  {errors.minatInvestasi && (
                    <p className="text-red-500 text-xs mt-0.5">{errors.minatInvestasi}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Layanan yang diminati?
                  </label>
                  <div className="space-y-1">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="minatTabungan"
                        checked={formData.minatTabungan}
                        onChange={handleInputChange}
                        className="w-3 h-3 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="ml-1.5 text-xs text-gray-700">
                        Tabungan Qurban
                      </span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="minatKonsultasi"
                        checked={formData.minatKonsultasi}
                        onChange={handleInputChange}
                        className="w-3 h-3 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="ml-1.5 text-xs text-gray-700">
                        Konsultasi Ternak
                      </span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="minatProduk"
                        checked={formData.minatProduk}
                        onChange={handleInputChange}
                        className="w-3 h-3 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="ml-1.5 text-xs text-gray-700">
                        Pembelian Produk
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-2 lg:space-x-3 pt-2 lg:pt-4">
              <button
                type="button"
                onClick={onBack}
                className="flex-1 bg-gray-200 text-gray-700 font-semibold py-2 lg:py-3 px-3 lg:px-4 rounded-md hover:bg-gray-300 transition-colors text-xs lg:text-sm"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-primary text-white font-semibold py-2 lg:py-3 px-3 lg:px-4 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-xs lg:text-sm"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                    Kirim...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane mr-1"></i>
                    Daftar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrasiForm;
